/**
 * O painel "Dados do contato" da conversa de WhatsApp (13.3, chamado de 21/10, P1; FR-40, PN2).
 *
 * Na conversa não havia onde ver o número do contato que tem nome, nem o que o CRM sabe dele, e a
 * Pessoa solta — a que nasceu com o número por nome quando escreveu pelo WhatsApp — não se editava
 * nem se vinculava por tela nenhuma. O painel abre pelo nome do cabeçalho do chat ou pelo menu de
 * clique direito da linha (no celular, "⋯"), e mostra, edita e vincula.
 *
 * **A composição é do SPA (AD-25), e cada dado vem do dono dele:**
 *
 * - a `Conversa` aberta, do `comunicacao`: o número, o nome no WhatsApp, `pessoa_id`;
 * - a Pessoa, do `cadastro`, pelo lote `GET /pessoas?ids=` — **relida ao abrir** (`staleTime: 0`),
 *   antes do modal da Empresa e antes de "Vincular": a pergunta é "ela ainda está solta agora?";
 * - as Empresas, pelas `empresa_ids` do `cadastro` (13.19c2): as que quem olha enxerga, na ordem do
 *   vínculo — "Empresa" (ou "Empresas", com mais de uma) mostra a primeira (`useEmpresa`), com o
 *   link e os Negócios em andamento dela, e um "+N" que abre as demais no mesmo painel, cada uma com
 *   os seus. Uma Empresa invisível não vem do serviço: não é nomeada nem contada. "Solta" é a lista
 *   vazia (`ehSolta`). O aviso de réplica atrasada só aparece quando a Pessoa tem Empresa visível e
 *   a conversa ainda não tem nenhuma;
 * - os Negócios em andamento, por `useNegociosDaEmpresa`, com o nome da Etapa vindo de `useFunis`.
 *
 * **Nenhum valor, para perfil nenhum** (D-7): os Negócios aparecem como "Negócio #código · Etapa".
 *
 * **A edição** ("Editar", só com `editar_empresa` na sessão — conveniência, e não autorização) usa
 * os campos da ficha (`QuatroCampos` na variante `telefoneSoLeitura`): nome, cargo e e-mail. A solta grava
 * por `PUT /pessoas/{id}` (D-1); a vinculada pela rota da Empresa, onde a RLS da ficha vale e o ERP
 * fica sabendo — **a primeira das `empresa_ids_editaveis`** (13.19c2), nunca uma vista só pela PA-3:
 * quem só lê a Pessoa por uma Tarefa aberta não tem "Editar" nem aplica etiqueta. Salvar relê as
 * listas de Pessoas de todas as Empresas visíveis dela. O `409` da solta (outra aba a vinculou) relê a Pessoa, passa ao modo vinculada e
 * mantém o digitado. Na solta cujo nome não tem letra, o campo Nome abre com o nome do WhatsApp,
 * marcado como sugestão (D-12, fecha a DW-361): só grava se a pessoa salvar.
 *
 * **O contato do Negócio sem Empresa recebido é só leitura** (13.20c, decisão do Gustavo de
 * 26/10: quem recebe o Negócio "passa a ver o contato dele e a ler a conversa com ele"). Quem
 * decide é o `pode_editar` da Pessoa (code review da 13.20c, A4): falso para a Pessoa que quem olha
 * lê só pelo ramo do Negócio sem Empresa — em qualquer conversa, inclusive a do próprio número. O
 * painel mostra os dados e as etiquetas, sem "Editar", sem aplicar etiqueta e sem "Vincular" (o
 * vínculo é o "Definir a Empresa", 13.20b). Conveniência: o servidor recusa as escritas com o
 * `404` de Pessoa.
 *
 * **O telefone é só leitura** (13.17, chamado de 26/10, item M: "telefone aqui não deve ser
 * editável [...] porque aí significa que é outro cliente"). Na edição ele é texto, no lugar de
 * sempre (entre Cargo e E-mail), e mostra o telefone **gravado** — o número da conversa é o do topo,
 * em "Número". O `PUT` leva o gravado. A Pessoa gravada sem telefone, ou com um telefone sem chave
 * (só texto, sem dígito), mostra e envia o número da conversa (`telefoneDaConversaNaFicha`), e
 * passa a tê-lo; se nem a conversa tem dígito, nada vai, e a recusa "Informe o telefone da pessoa."
 * aparece. O servidor confere: a troca de chave pela rota da solta é `422` `telefone_da_conversa`,
 * e receber um número que já é de outra Pessoa é `409` `telefone_de_outra_pessoa`. Saiu o "Salvar
 * assim mesmo" da 13.14a (DW-385). A régua do vazio é a de sempre (`recusaDaPessoa`). **Na
 * vinculada**, o só-leitura é desta tela: a rota da ficha da Empresa continua aceitando trocar o
 * telefone, onde ele segue editável (decisão de 26/10).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useBlocker } from '@tanstack/react-router'
import {
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { telefoneDaConversaNaFicha } from '@/modulos/cadastro/ModalDaNovaEmpresa'
import { EtiquetasDoAlvo } from '@/modulos/cadastro/etiquetas'
import { nomeExibidoDaEmpresa } from '@/modulos/cadastro/nome-da-empresa'
import {
  VinculoDaPessoaSolta,
} from '@/modulos/cadastro/VinculoDaPessoaSolta'
import type { EmpresaEscolhida } from '@/modulos/cadastro/CampoDeEmpresa'
import {
  type CampoDaRecusa,
  QuatroCampos,
  type Rascunho,
  campoDaFalha,
  campoDaRecusaLocal,
  corpoDe,
  recusaDaPessoa,
  semNada,
} from '@/modulos/cadastro/ResponsaveisDaEmpresa'
import {
  type EdicaoDePessoa,
  type Pessoa,
  chaveDasPessoas,
  editarPessoa,
  ehSolta,
  empresaIdsDe,
  empresaDaEdicao,
  etiquetaIdsDe,
  lerPessoasPorIds,
  useEditarPessoaSolta,
  useEmpresa,
} from '@/modulos/cadastro/api'
import { copiarParaAreaDeTransferencia } from '@/modulos/negocios/copiar'
import {
  type NegocioNoQuadro,
  useDefinirAEmpresa,
  useFunis,
  useNegociosDaEmpresa,
  useNegociosDaPessoa,
} from '@/modulos/negocios/api'
import { EMPRESA_A_DEFINIR } from '@/modulos/negocios/apresentacao'
import {
  ABRIR_A_FICHA_DA_EMPRESA,
  CONFERIR_DE_NOVO,
  CONTATO_AINDA_NAO_VINCULADO,
  VINCULANDO_O_CONTATO,
  useEsperaDoVinculo,
} from '@/modulos/negocios/espera-do-vinculo'
import { ehDeGestao } from '@/shell/navegacao'
import { useAbrirNegocio } from '@/modulos/negocios/navegacao-do-negocio'
import { useSessao } from '@/modulos/identidade/sessao'
import { chaveDoTelefone } from '@/telefone/chave-do-telefone'
import { telefoneNaTela } from '@/modulos/cadastro/telefone-na-tela'
import { CHAVE_DAS_CONVERSAS, type Conversa } from './api'
import { formatarNumeroDeWhatsapp } from './contexto-da-conversa'

/** O nome do painel — o título, o rótulo da região e o item do menu da linha. */
export const DADOS_DO_CONTATO = 'Dados do contato'
/** A descrição acessível do nome no cabeçalho do chat: é ele que abre o painel. */
export const VER_OS_DADOS_DO_CONTATO = 'Ver os dados do contato'
/** O "⋯" de cada linha no celular — fora do botão da linha, com o nome do contato. */
export const maisOpcoesDe = (nome: string): string => `Mais opções de ${nome}`
/** O nome acessível do menu da linha. */
export const opcoesDe = (nome: string): string => `Opções de ${nome}`

export const FECHAR_O_PAINEL = 'Fechar'
/** No celular o painel ocupa a tela no lugar do chat, e a saída volta para a conversa. */
export const VOLTAR_PARA_A_CONVERSA = '← Conversa'

export const ROTULO_DO_NUMERO = 'Número'
export const COPIAR_NUMERO = 'Copiar número'
export const NUMERO_COPIADO = 'Número copiado.'
export const NUMERO_NAO_COPIADO = 'Não foi possível copiar; selecione o número.'
/**
 * O campo único do nome (29/10/2026, DW-613 — o Gustavo: "vai ser só Nome, o nome no whatsapp entra
 * automaticamente, e ai tem a opção de mudar"). É o nome da Pessoa no `cadastro`, o mesmo da tela
 * Pessoas; o do WhatsApp só aparece nele enquanto a Pessoa não tem nome com letra, ou quando a
 * conversa não tem Pessoa que quem olha alcance.
 */
export const ROTULO_DO_NOME = 'Nome'
export const ROTULO_DA_EMPRESA = 'Empresa'
/** O rótulo com mais de uma Empresa visível (13.19c2). */
export const ROTULO_DAS_EMPRESAS = 'Empresas'
/** O nome acessível do "+N" do painel: abre (ou fecha) as demais Empresas visíveis (13.19c2). */
export function mostrarAsDemaisEmpresas(quantas: number): string {
  return quantas === 1 ? 'Mostrar mais 1 empresa' : `Mostrar mais ${String(quantas)} empresas`
}
export const ROTULO_DOS_NEGOCIOS = 'Negócios em andamento'
/** O campo vazio — o traço, como nas fichas do CRM. */
const VAZIO = '—'

/** Sem `pessoa_id`: o `cadastro` ainda não respondeu de quem é o número (D-11). */
export const SEM_PESSOA = 'O cadastro ainda não identificou este contato.'
/** A Pessoa que o lote não devolveu: a RLS a esconde de quem olha (a solta de outro recebedor). */
export const PESSOA_ILEGIVEL = 'Os dados do cadastro deste contato não estão disponíveis para você.'
/** O lote falhou: não é sigilo, é a resposta que não chegou. */
export const PESSOA_INDISPONIVEL = 'Não foi possível carregar os dados do cadastro deste contato.'
export const TENTAR_DE_NOVO = 'Tentar de novo'
const CARREGANDO_O_CONTATO = 'Carregando os dados do contato…'
const CARREGANDO_A_EMPRESA = 'Carregando a Empresa…'
const CARREGANDO_OS_NEGOCIOS = 'Carregando os negócios…'
export const NEGOCIOS_INDISPONIVEIS_NO_PAINEL =
  'Não foi possível carregar os negócios desta empresa agora.'
export const SEM_NEGOCIOS_NO_PAINEL = 'Nenhum negócio em andamento que você possa ver.'

/** "Negócio #31 · FUP" — sem valor nenhum (D-7). Sem a Etapa conhecida, só o código. */
export function rotuloDoNegocioNoPainel(codigo: number, etapa: string | undefined): string {
  const base = `Negócio #${String(codigo)}`
  return etapa === undefined ? base : `${base} · ${etapa}`
}

export const EDITAR = 'Editar'
export const SALVAR = 'Salvar'
export const SALVANDO = 'Salvando…'
export const CANCELAR_A_EDICAO = 'Cancelar'
/** A marca do nome que veio do WhatsApp para o campo Nome da solta (D-12). */
export const SUGESTAO_DO_WHATSAPP = 'Sugestão do WhatsApp'

/*
 * Os textos do vínculo moram no componente extraído (13.4) e são reexportados daqui: os testes e os
 * hospedeiros de antes continuam importando do painel.
 */
export {
  AVISO_EMPRESA_DE_OUTRO,
  CADASTRAR_EMPRESA_COM_O_CONTATO,
  VINCULANDO,
  VINCULAR,
  VINCULAR_A_EXISTENTE,
  contatoVinculadoA,
  empresaCriadaSemOContato,
} from '@/modulos/cadastro/VinculoDaPessoaSolta'


/** Por que "Fechar" (ou "← Conversa") está desabilitado: a edição alterada se perderia. */
export const SALVE_OU_CANCELE_ANTES_DE_FECHAR = 'Salve ou cancele a edição antes de fechar.'

/**
 * A réplica do `comunicacao` ainda não recebeu o vínculo: o atraso é dito, e não escondido. Desde o
 * code review da 13.19c2 (B) ele diz o efeito de verdade — quem lê a conversa — e não promete a
 * linha do cabeçalho, que segue o recorte do `comunicacao`.
 */
export const ATRASO_DA_REPLICA =
  'O WhatsApp ainda não recebeu o vínculo deste contato; até lá, quem responde pela empresa ainda não vê esta conversa.'
/**
 * O "Salvar" da vinculada sem Empresa editável (code review da 13.19c2, E5): a Pessoa relida passou
 * a ser vista só por uma Empresa que quem olha não edita (a PA-3). A edição fecha, e diz por quê.
 */
export const SEM_EMPRESA_PARA_EDITAR =
  'Você não edita este contato por nenhuma das empresas dele que você vê.'
/** A conversa saiu da lista de quem vinculou (FR-39): o aviso fica no topo da página. */
export const CONVERSA_SAIU_DA_LISTA =
  'A conversa saiu da sua lista: ela agora segue a empresa vinculada.'

/** O `409` do envelope único: na edição da solta, ela foi vinculada noutra aba. */
const STATUS_DE_CONFLITO = 409

/** O `id` da frase que explica por que "Fechar" está desabilitado. */
const ID_DO_MOTIVO_DO_FECHAR = 'contato-motivo-do-fechar'

/** O `id` do botão do nome no cabeçalho do chat — para onde o foco volta quando o painel fecha. */
export const ID_DO_NOME_DO_CONTATO = 'conversas-nome-do-contato'

/** A permissão das escritas de Pessoa (FR-61-A: "Editar Empresa e Responsáveis pela empresa"). */
const PERMISSAO_DE_EDITAR = 'editar_empresa'

/**
 * A largura até a qual o painel é **gaveta**, e não terceira coluna. Escrita como `max-width`
 * porque é a única forma de consulta que o CRM usa (e a que o dublê do jsdom entende).
 */
const CONSULTA_DA_GAVETA = '(max-width: 1199px)'

/** Como o painel se dispõe: terceira coluna, gaveta sobre o chat, ou a tela inteira do celular. */
export type ModoDoPainel = 'coluna' | 'gaveta' | 'tela'

/** O modo do painel na largura de agora. A virada é de árvore e atributo, como a do shell. */
export function useModoDoPainel(ehCelular: boolean): ModoDoPainel {
  const estreito = useSyncExternalStore(
    (avisar) => {
      const consulta = window.matchMedia(CONSULTA_DA_GAVETA)
      consulta.addEventListener('change', avisar)
      return () => {
        consulta.removeEventListener('change', avisar)
      }
    },
    () => window.matchMedia(CONSULTA_DA_GAVETA).matches,
    () => false,
  )
  if (ehCelular) {
    return 'tela'
  }
  return estreito ? 'gaveta' : 'coluna'
}

/** "Tem letra" — o mesmo critério de nome de verdade de `contexto-da-conversa.ts`. */
function temLetra(texto: string | null | undefined): texto is string {
  return texto !== null && texto !== undefined && /\p{L}/u.test(texto)
}

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * O telefone que o painel mostra e o `PUT` leva (13.17): o **gravado**, como está. Na Pessoa sem
 * telefone — vazio pela régua de `semNada`, ou sem chave (só texto, sem dígito) —, o número da
 * conversa na grafia da ficha; e, se nem ele tem dígito, nenhum (`''`): a régua recusa antes de
 * enviar, em vez de mandar ao servidor um telefone que ele recusaria.
 */
export function telefoneDoPainel(pessoa: Pessoa, numeroDaConversa: string): string {
  const gravado = pessoa.telefone ?? ''
  if (semNada(gravado) !== '' && chaveDoTelefone(gravado) !== null) {
    return gravado
  }
  return chaveDoTelefone(numeroDaConversa) === null
    ? ''
    : telefoneDaConversaNaFicha(numeroDaConversa)
}

function rascunhoDe(pessoa: Pessoa, telefone: string, nome: string = pessoa.nome): Rascunho {
  return {
    nome,
    telefone,
    email: pessoa.email ?? '',
    cargo: pessoa.cargo ?? '',
  }
}

function mesmoCorpo(um: EdicaoDePessoa, outro: EdicaoDePessoa): boolean {
  return (
    um.nome === outro.nome &&
    um.telefone === outro.telefone &&
    um.email === outro.email &&
    um.cargo === outro.cargo
  )
}

/** Uma linha do painel: o rótulo e o valor, numa lista de definições. */
function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="contato__linha">
      <dt className="contato__rotulo">{rotulo}</dt>
      <dd className="contato__valor">{children}</dd>
    </div>
  )
}

/**
 * Uma Empresa da Pessoa e os Negócios em andamento dela — sem valor nenhum (D-7). `junto` vai ao lado
 * do link (o "+N" da primeira, 13.19c2).
 */
function EmpresaENegocios({
  empresaId,
  rotulo = ROTULO_DA_EMPRESA,
  junto,
}: {
  empresaId: string
  rotulo?: string
  junto?: React.ReactNode
}) {
  const empresa = useEmpresa(empresaId)
  const negocios = useNegociosDaEmpresa(empresaId)
  const funis = useFunis()
  const abrir = useAbrirNegocio()
  const etapas = new Map(
    (funis.data ?? []).flatMap((funil) => funil.etapas.map((etapa) => [etapa.id, etapa.nome])),
  )

  return (
    <>
      <Linha rotulo={rotulo}>
        {empresa.isPending ? (
          CARREGANDO_A_EMPRESA
        ) : empresa.isError ? (
          <span role="alert">{mensagemDe(empresa.error)}</span>
        ) : (
          <Link
            to="/empresas/$empresaId"
            params={{ empresaId: empresa.data.id }}
            className="contato__link"
          >
            {nomeExibidoDaEmpresa(empresa.data)}
          </Link>
        )}
        {junto}
      </Linha>
      <Linha rotulo={ROTULO_DOS_NEGOCIOS}>
        {negocios.isPending ? (
          CARREGANDO_OS_NEGOCIOS
        ) : negocios.isError ? (
          NEGOCIOS_INDISPONIVEIS_NO_PAINEL
        ) : negocios.data.length === 0 ? (
          SEM_NEGOCIOS_NO_PAINEL
        ) : (
          <ul className="contato__negocios">
            {negocios.data.map((negocio) => (
              <li key={negocio.id}>
                <button
                  type="button"
                  className="contato__negocio"
                  onClick={() => {
                    abrir(negocio.id)
                  }}
                >
                  {rotuloDoNegocioNoPainel(negocio.codigo, etapas.get(negocio.etapa_id))}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Linha>
    </>
  )
}

/**
 * O Negócio sem Empresa do contato (13.20b2, FR-84): "Empresa a definir" e os Negócios em andamento
 * sem Empresa que quem olha vê (`GET /negocios?pessoa_id=`) — ao lado das Empresas dela, no mesmo
 * desenho, sem valor nenhum (D-7). Cada Negócio abre o modal.
 */
function NegociosSemEmpresa({ negocios }: { negocios: readonly NegocioNoQuadro[] }) {
  const funis = useFunis()
  const abrir = useAbrirNegocio()
  const etapas = new Map(
    (funis.data ?? []).flatMap((funil) => funil.etapas.map((etapa) => [etapa.id, etapa.nome])),
  )
  if (negocios.length === 0) {
    return null
  }
  return (
    <dl className="contato__dados" data-sem-empresa="">
      <Linha rotulo={ROTULO_DA_EMPRESA}>
        <span className="contato__sem-empresa">{EMPRESA_A_DEFINIR}</span>
      </Linha>
      <Linha rotulo={ROTULO_DOS_NEGOCIOS}>
        <ul className="contato__negocios">
          {negocios.map((negocio) => (
            <li key={negocio.id}>
              <button
                type="button"
                className="contato__negocio"
                onClick={() => {
                  abrir(negocio.id)
                }}
              >
                {rotuloDoNegocioNoPainel(negocio.codigo, etapas.get(negocio.etapa_id))}
              </button>
            </li>
          ))}
        </ul>
      </Linha>
    </dl>
  )
}

/**
 * As Empresas visíveis da Pessoa no painel (13.19c2): "Empresa" com uma; "Empresas" com mais, a
 * primeira com o link e os Negócios dela e um "+N" que abre as demais logo abaixo, cada uma com os
 * seus. Só vêm as que quem olha enxerga — a invisível não é nomeada nem contada.
 */
function EmpresasDaPessoa({ empresaIds }: { empresaIds: readonly string[] }) {
  const [abertas, setAbertas] = useState(false)
  const [primeira, ...demais] = empresaIds
  if (primeira === undefined) {
    return null
  }
  const idDasDemais = `contato-demais-empresas-${primeira}`
  return (
    <>
      <dl className="contato__dados">
        <EmpresaENegocios
          empresaId={primeira}
          rotulo={demais.length === 0 ? ROTULO_DA_EMPRESA : ROTULO_DAS_EMPRESAS}
          junto={
            demais.length === 0 ? null : (
              <button
                type="button"
                className="etiqueta etiqueta--mais contato__mais-empresas"
                aria-expanded={abertas}
                aria-controls={idDasDemais}
                aria-label={mostrarAsDemaisEmpresas(demais.length)}
                onClick={() => {
                  setAbertas((atual) => !atual)
                }}
              >
                {`+${String(demais.length)}`}
              </button>
            )
          }
        />
      </dl>
      {demais.length === 0 ? null : (
        <div id={idDasDemais} className="contato__demais-empresas" hidden={!abertas}>
          {abertas
            ? demais.map((empresaId) => (
                <dl key={empresaId} className="contato__dados">
                  <EmpresaENegocios empresaId={empresaId} />
                </dl>
              ))
            : null}
        </div>
      )}
    </>
  )
}

export interface PropriedadesDosDadosDoContato {
  conversa: Conversa
  modo: ModoDoPainel
  /** O aviso do vínculo feito nesta conversa ("Contato vinculado a …"), guardado em `Conversas`. */
  avisoDoVinculo?: string
  aoFechar: () => void
  /** O vínculo saiu: `Conversas` guarda o aviso, que sobrevive à conversa sair da lista. */
  aoVincular: (nomeDaEmpresa: string) => void
  /**
   * 13.14c (DW-380): no celular o painel é uma entrada do histórico (`?dados=1`), e o "voltar" que
   * a tiraria com a edição alterada, o envio em curso ou o modal do vínculo aberto é segurado — a
   * mesma guarda do "← Conversa".
   */
  bloquearOVoltar?: boolean
}

export function DadosDoContato({
  conversa,
  modo,
  avisoDoVinculo: avisoGuardado,
  aoFechar,
  aoVincular,
  bloquearOVoltar = false,
}: PropriedadesDosDadosDoContato) {
  const { data: sessao } = useSessao()
  const temAPermissao = sessao !== undefined && PERMISSAO_DE_EDITAR in sessao.permissoes
  const clienteDeConsultas = useQueryClient()
  const titulo = useRef<HTMLHeadingElement>(null)

  const numero = formatarNumeroDeWhatsapp(conversa.numero)
  const nomeNoWhatsapp = conversa.nome_no_whatsapp ?? null
  const pessoaId = conversa.pessoa_id

  /*
   * A Pessoa, relida **ao abrir**: chave própria debaixo de `['cadastro', 'pessoas']` — o prefixo
   * que a edição e o vínculo invalidam —, e não o lote da lista, que guarda o nome por cinco
   * minutos. Aqui a pergunta é o estado de agora.
   */
  const consulta = useQuery({
    queryKey: ['cadastro', 'pessoas', 'painel', pessoaId ?? ''],
    queryFn: () => lerPessoasPorIds([pessoaId as string]),
    enabled: pessoaId !== null,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
  const pessoa: Pessoa | undefined = consulta.data?.[0]
  /* 13.19c2: "solta" é a lista vazia; a edição da vinculada vai pela primeira Empresa que quem olha
     **edita** — nunca uma vista só pela PA-3, e sem nenhuma, não há edição (nem etiqueta). */
  const solta = pessoa !== undefined && ehSolta(pessoa)
  const empresaDeEdicao = pessoa === undefined ? undefined : empresaDaEdicao(pessoa)
  /* 13.20c (code review A4): a Pessoa que quem olha lê **só** pelo Negócio sem Empresa que
     recebeu é só leitura — o `pode_editar` do servidor, e não a conversa (a Responsável pode abrir a
     Pessoa do ramo também na conversa do próprio número). Só o `false` explícito trava: o servidor
     anterior ao campo não o manda, e aí quem decide é a recusa do servidor. */
  const soLeitura = pessoa !== undefined && (pessoa as Partial<Pessoa>).pode_editar === false
  const podeEditar = temAPermissao && !soLeitura && (solta || empresaDeEdicao !== undefined)

  /*
   * 13.20b2 (FR-84): os Negócios em andamento do contato (`?pessoa_id=`, code review A1): o sem
   * Empresa e o ex-N0, que recebeu a Empresa e espera o contato chegar a ela.
   *
   * - **O N0 que quem age define** — a Responsável com `editar_negocio`, ou a gestão (escopo
   *   `todas` com `trocar_empresa_do_negocio`) quando há um N0 só (B4) — muda o vincular da solta:
   *   a Empresa escolhida é **definida** nele, e o `cadastro` vincula o contato pelo evento.
   * - **O ex-N0 cujo contato ainda não está na Empresa** mostra a espera ("vinculando…" e, no teto,
   *   a saída com o caminho para a ficha da Empresa) — dos dados, e não de um estado desta
   *   abertura: reaberto o painel, ela continua, e o vincular de antes não volta.
   * - **O recorte que falhou é "não sei"** (A2): a mensagem e "Tentar de novo", sem vincular.
   */
  const doContato = useNegociosDaPessoa(pessoaId)
  const negociosDoContato = doContato.data ?? []
  const negociosSemEmpresa = negociosDoContato.filter((negocio) => negocio.empresa_id === null)
  const comoGestao =
    sessao !== undefined &&
    ehDeGestao(sessao.escopo_de_carteira) &&
    'trocar_empresa_do_negocio' in sessao.permissoes
  const daResponsavel =
    sessao !== undefined && 'editar_negocio' in sessao.permissoes
      ? negociosSemEmpresa.find((negocio) => negocio.responsavel_id === sessao.usuario_id)
      : undefined
  const [soDaGestao] = negociosSemEmpresa
  const negocioQueQuemAgeDefine =
    daResponsavel ?? (comoGestao && negociosSemEmpresa.length === 1 ? soDaGestao : undefined)
  const definicao = useDefinirAEmpresa(negocioQueQuemAgeDefine?.id ?? '')
  const empresasDoContato = pessoa === undefined ? [] : (empresaIdsDe(pessoa) ?? [])
  const empresaAguardada =
    negociosDoContato.find(
      (negocio) => negocio.empresa_id !== null && !empresasDoContato.includes(negocio.empresa_id),
    )?.empresa_id ?? null
  const aguardando = pessoaId !== null && !consulta.isPending && empresaAguardada !== null
  const esperaDoVinculo = useEsperaDoVinculo(aguardando ? pessoaId : null, empresaAguardada)
  /* O vínculo chegou: o aviso de sempre ("Contato vinculado a …"), guardado em `Conversas`, uma
     vez por Empresa esperada nesta abertura. A Pessoa relida deixa de ser solta, e a espera some. */
  const [esperada, setEsperada] = useState<string | null>(null)
  if (aguardando && empresaAguardada !== esperada) {
    setEsperada(empresaAguardada)
  }
  const chegou = esperada !== null && !aguardando && empresasDoContato.includes(esperada)
  const daEsperada = useEmpresa(esperada ?? undefined)
  const nomeDaEsperada = daEsperada.data === undefined ? null : nomeExibidoDaEmpresa(daEsperada.data)
  const avisadoDe = useRef<string | null>(null)
  useEffect(() => {
    if (!chegou || nomeDaEsperada === null || avisadoDe.current === esperada) {
      return
    }
    avisadoDe.current = esperada
    aoVincular(nomeDaEsperada)
  }, [chegou, nomeDaEsperada, esperada, aoVincular])
  const blocoDaEspera = doContato.isError ? (
    <p className="contato__erro" role="alert">
      <span>{mensagemDe(doContato.error)}</span>{' '}
      <button
        type="button"
        className="contato__acao"
        onClick={() => {
          void doContato.refetch()
        }}
      >
        {TENTAR_DE_NOVO}
      </button>
    </p>
  ) : !aguardando || empresaAguardada === null ? null : (
    <div className="contato__nota contato__espera">
      {/* Uma região `status` só, trocando o texto; os caminhos ficam fora dela. */}
      <span role="status">
        {esperaDoVinculo.estado === 'demorando' ? CONTATO_AINDA_NAO_VINCULADO : VINCULANDO_O_CONTATO}
      </span>
      {esperaDoVinculo.estado === 'demorando' ? (
        <>
          <Link
            to="/empresas/$empresaId"
            params={{ empresaId: empresaAguardada }}
            className="contato__link"
          >
            {ABRIR_A_FICHA_DA_EMPRESA}
          </Link>
          <button
            type="button"
            className="contato__acao"
            onClick={esperaDoVinculo.conferirDeNovo}
          >
            {CONFERIR_DE_NOVO}
          </button>
        </>
      ) : null}
    </div>
  )

  /* --- A cópia do número (D-8: como exibido) --- */
  const [copia, setCopia] = useState('')

  /* --- A edição --- */
  const [rascunho, setRascunho] = useState<Rascunho | null>(null)
  const [sugerido, setSugerido] = useState(false)
  const [recusaDaEdicao, setRecusaDaEdicao] = useState<string | null>(null)
  /** O aviso fora do formulário: a edição que fechou por falta de Empresa editável (E5). */
  const [avisoDaEdicao, setAvisoDaEdicao] = useState<string | null>(null)
  /** O campo de que a recusa da edição fala (13.17): a local, ou a do código do serviço. */
  const [campoDaRecusaDaEdicao, setCampoDaRecusaDaEdicao] = useState<CampoDaRecusa>(null)
  const edicaoDaSolta = useEditarPessoaSolta()
  const edicaoDaVinculada = useMutation({
    mutationFn: ({ empresaId, corpo }: { empresaId: string; corpo: EdicaoDePessoa }) =>
      editarPessoa(empresaId, pessoaId as string, corpo),
    /* 13.19c2: a ficha de cada Empresa visível da Pessoa mostra o contato — todas são relidas, e não
       só a da edição. */
    onSuccess: (editada, { empresaId }) =>
      Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'pessoas'] }),
        ...[...new Set([empresaId, ...(empresaIdsDe(editada) ?? [])])].map((id) =>
          clienteDeConsultas.invalidateQueries({ queryKey: chaveDasPessoas(id) }),
        ),
      ]),
  })
  const salvando = edicaoDaSolta.isPending || edicaoDaVinculada.isPending
  /*
   * O telefone não é campo (13.17): o rascunho o carrega fixo, e a comparação usa o mesmo dos dois
   * lados — a Pessoa sem telefone, que vai receber o da conversa, não conta como "edição alterada"
   * só por abrir a edição.
   */
  const telefoneFixo = pessoa === undefined ? '' : telefoneDoPainel(pessoa, conversa.numero)
  const corpoGravado =
    pessoa === undefined ? null : corpoDe(rascunhoDe(pessoa, telefoneFixo), pessoa.telefone)
  const edicaoAlterada =
    rascunho !== null &&
    corpoGravado !== null &&
    pessoa !== undefined &&
    !mesmoCorpo(corpoDe(rascunho, pessoa.telefone), corpoGravado)

  function abrirAEdicao(atual: Pessoa) {
    setAvisoDaEdicao(null)
    /* Code review de 29/10/2026: a sugestão vale também para a vinculada sem nome com letra — o
       campo "Nome" do painel já mostra o do WhatsApp, e o que se vê é o que se edita. */
    const sugere = !temLetra(atual.nome) && temLetra(nomeNoWhatsapp)
    setSugerido(sugere)
    setRecusaDaEdicao(null)
    setRascunho(
      rascunhoDe(
        atual,
        telefoneDoPainel(atual, conversa.numero),
        sugere ? nomeNoWhatsapp : atual.nome,
      ),
    )
  }

  async function salvar(evento: FormEvent<HTMLFormElement> | null) {
    evento?.preventDefault()
    if (rascunho === null || pessoa === undefined || salvando) {
      return
    }
    //: A régua única (13.17): o nome e o telefone fixo pelo `semNada`, como na ficha.
    const local = recusaDaPessoa(rascunho)
    if (local !== null) {
      setRecusaDaEdicao(local)
      setCampoDaRecusaDaEdicao(campoDaRecusaLocal(local))
      return
    }
    setRecusaDaEdicao(null)
    setCampoDaRecusaDaEdicao(null)
    const corpo = corpoDe(rascunho, pessoa.telefone)
    try {
      if (solta) {
        await edicaoDaSolta.mutateAsync({ pessoaId: pessoa.id, pessoa: corpo })
      } else if (empresaDeEdicao !== undefined) {
        await edicaoDaVinculada.mutateAsync({ empresaId: empresaDeEdicao, corpo })
      } else {
        /* Sem Empresa editável (E5): não fica mudo — a edição fecha e o porquê aparece. */
        setRascunho(null)
        setSugerido(false)
        setAvisoDaEdicao(SEM_EMPRESA_PARA_EDITAR)
        return
      }
      setRascunho(null)
      setSugerido(false)
    } catch (falha) {
      setRecusaDaEdicao(mensagemDe(falha))
      setCampoDaRecusaDaEdicao(campoDaFalha(falha))
      if (
        falha instanceof ErroDaApi &&
        falha.status === STATUS_DE_CONFLITO &&
        solta
      ) {
        /* Ela foi vinculada noutra aba: relê, e o próximo "Salvar" vai pela rota da Empresa. O
           digitado fica — e o telefone fixo passa a ser o da Pessoa relida (13.17). */
        const relida = (await consulta.refetch()).data?.[0]
        if (relida !== undefined) {
          setRascunho((atual) =>
            atual === null
              ? atual
              : { ...atual, telefone: telefoneDoPainel(relida, conversa.numero) },
          )
        }
      }
    }
  }

  /* --- O vínculo da solta (o `VinculoDaPessoaSolta`, extraído na 13.4) --- */
  /*
   * O desfecho terminal de um vínculo que não pode mais acontecer — o `409`/`404` do serviço (o
   * contato já está noutra Empresa) ou a ficha nova que nasceu depois de o contato ir para outra. Ele
   * mora fora da seção do vínculo porque o painel passa ao modo vinculada, e a seção some.
   */
  const [recusaDoVinculo, setRecusaDoVinculo] = useState<string | null>(null)
  /*
   * O vínculo em curso e o modal do "Cadastrar Empresa" aberto, que o componente informa. Com o
   * modal aberto a seção **fica montada** mesmo que a Pessoa deixe de ser solta: a linha de Pessoa
   * do modal com o número da conversa a adota (13.4), e desmontar a seção ali desmontaria o modal
   * antes do fim da cadeia — e o "Contato vinculado" nunca apareceria.
   */
  const [vinculoEmCurso, setVinculoEmCurso] = useState({ ocupado: false, modalAberto: false })
  const aoMudarOcupacao = useCallback((estado: { ocupado: boolean; modalAberto: boolean }) => {
    if (estado.ocupado) {
      setRecusaDoVinculo(null)
    }
    setVinculoEmCurso((atual) =>
      atual.ocupado === estado.ocupado && atual.modalAberto === estado.modalAberto ? atual : estado,
    )
  }, [])
  const relerAPessoa = useCallback(async () => (await consulta.refetch()).data?.[0], [consulta])

  /* --- O foco, o Escape e o "Fechar" --- */
  const ocupado = salvando || vinculoEmCurso.ocupado
  /* A guarda única do Escape e do "Fechar": edição alterada ou envio em curso. */
  const fechamentoBloqueado = edicaoAlterada || ocupado
  /* O "voltar" do celular (13.14c): a guarda é lida na hora da navegação — ref, e não estado. */
  const voltarBloqueado = useRef(false)
  useEffect(() => {
    voltarBloqueado.current =
      bloquearOVoltar && (fechamentoBloqueado || vinculoEmCurso.modalAberto)
  })
  useBlocker({
    shouldBlockFn: ({ current, next }) =>
      voltarBloqueado.current &&
      current.pathname === next.pathname &&
      (current.search as { dados?: unknown }).dados === 1 &&
      (next.search as { dados?: unknown }).dados !== 1,
    enableBeforeUnload: false,
  })
  useEffect(() => {
    titulo.current?.focus()
  }, [])

  function aoTeclar(evento: KeyboardEvent<HTMLElement>) {
    if (evento.key !== 'Escape' || evento.defaultPrevented) {
      return
    }
    // Não fecha com modal ou menu por cima, com a edição alterada ou com envio em curso.
    if (vinculoEmCurso.modalAberto || fechamentoBloqueado) {
      return
    }
    evento.preventDefault()
    aoFechar()
  }

  const razaoDaFicha = temLetra(pessoa?.nome)
    ? (pessoa?.nome ?? '')
    : temLetra(nomeNoWhatsapp)
      ? nomeNoWhatsapp
      : ''

  return (
    <aside
      className="contato"
      data-modo={modo}
      aria-label={DADOS_DO_CONTATO}
      onKeyDown={aoTeclar}
    >
      <div className="contato__topo">
        <h2 className="contato__titulo" ref={titulo} tabIndex={-1}>
          {DADOS_DO_CONTATO}
        </h2>
        {/* A mesma guarda do Escape: com a edição alterada ou com envio em curso, fechar perderia
            o digitado (ou largaria o envio no meio). */}
        <button
          type="button"
          className="contato__fechar"
          disabled={fechamentoBloqueado}
          aria-describedby={edicaoAlterada ? ID_DO_MOTIVO_DO_FECHAR : undefined}
          onClick={aoFechar}
        >
          {modo === 'tela' ? VOLTAR_PARA_A_CONVERSA : FECHAR_O_PAINEL}
        </button>
      </div>
      {edicaoAlterada ? (
        <p className="contato__dica contato__motivo" id={ID_DO_MOTIVO_DO_FECHAR}>
          {SALVE_OU_CANCELE_ANTES_DE_FECHAR}
        </p>
      ) : null}

      <div className="contato__corpo">
        {avisoDaEdicao === null ? null : (
          <p className="contato__erro" role="alert">
            {avisoDaEdicao}
          </p>
        )}
        {recusaDoVinculo === null ? null : (
          <p className="contato__erro" role="alert">
            {recusaDoVinculo}
          </p>
        )}
        {avisoGuardado === undefined ? null : (
          <p className="contato__nota" role="status">
            {avisoGuardado}
          </p>
        )}

        <dl className="contato__dados">
          <Linha rotulo={ROTULO_DO_NUMERO}>
            <span className="contato__numero">{numero}</span>
            <button
              type="button"
              className="contato__acao"
              onClick={() => {
                void copiarParaAreaDeTransferencia(numero).then((ok) => {
                  setCopia(ok ? NUMERO_COPIADO : NUMERO_NAO_COPIADO)
                })
              }}
            >
              {COPIAR_NUMERO}
            </button>
            <span className="contato__copia" role="status">
              {copia}
            </span>
          </Linha>
          {/* Sem Pessoa alcançável (sem Pessoa, ou a que a policy não devolve): o Nome é o do
              WhatsApp, num rótulo só (DW-613). Com a Pessoa, ele mora nos dados dela, abaixo. */}
          {pessoaId === null || (!consulta.isPending && pessoa === undefined) ? (
            <Linha rotulo={ROTULO_DO_NOME}>
              {temLetra(nomeNoWhatsapp) ? nomeNoWhatsapp : VAZIO}
            </Linha>
          ) : null}
        </dl>

        {pessoaId === null ? (
          <p className="contato__aviso">{SEM_PESSOA}</p>
        ) : consulta.isPending ? (
          <p className="contato__aviso">{CARREGANDO_O_CONTATO}</p>
        ) : consulta.isError && pessoa === undefined ? (
          <p className="contato__erro" role="alert">
            {PESSOA_INDISPONIVEL}
            <button
              type="button"
              className="contato__acao"
              onClick={() => {
                void consulta.refetch()
              }}
            >
              {TENTAR_DE_NOVO}
            </button>
          </p>
        ) : pessoa === undefined ? (
          <>
            <p className="contato__aviso">{PESSOA_ILEGIVEL}</p>
            {/* Quem lia o contato só pelo ramo deixa de vê-lo entre a definição e o vínculo: a
                espera continua dita (code review, C). */}
            {blocoDaEspera}
          </>
        ) : (
          <>
            {rascunho === null ? (
              <dl className="contato__dados">
                <Linha rotulo={ROTULO_DO_NOME}>
                  {/* O nome da Pessoa; sem letra, o do WhatsApp; sem os dois, o telefone formatado —
                      a mesma regra da coluna Nome da tela Pessoas (code review de 29/10/2026). */}
                  {razaoDaFicha !== ''
                    ? razaoDaFicha
                    : pessoa.telefone
                      ? telefoneNaTela(pessoa.telefone)
                      : VAZIO}
                </Linha>
                <Linha rotulo="Cargo">{pessoa.cargo ?? VAZIO}</Linha>
                <Linha rotulo="Telefone">
                  {/* Formatado como no resto do SPA (`telefoneNaTela`): o número cru do provedor
                      que a solta carrega sai `+55 11 4034-8516` (29/10/2026). */}
                  {pessoa.telefone ? telefoneNaTela(pessoa.telefone) : VAZIO}
                </Linha>
                <Linha rotulo="E-mail">{pessoa.email ?? VAZIO}</Linha>
              </dl>
            ) : (
              <form
                className="contato__edicao"
                aria-label={`Editar ${pessoa.nome}`}
                onSubmit={(evento) => void salvar(evento)}
                noValidate
              >
                <QuatroCampos
                  sufixo="contato"
                  rascunho={rascunho}
                  //: 13.17: o gravado (ou o da conversa, na Pessoa sem telefone), no lugar de sempre.
                  telefoneSoLeitura={
                    rascunho.telefone === '' ? VAZIO : telefoneNaTela(rascunho.telefone)
                  }
                  aoTrocar={(campo, valor) => {
                    setRecusaDaEdicao(null)
                    setCampoDaRecusaDaEdicao(null)
                    if (campo === 'nome') {
                      setSugerido(false)
                    }
                    setRascunho((atual) => (atual === null ? atual : { ...atual, [campo]: valor }))
                  }}
                  recusaDoNome={
                    recusaDaEdicao !== null && campoDaRecusaDaEdicao === 'nome'
                      ? 'contato-recusa-do-nome'
                      : null
                  }
                  recusaDoTelefone={
                    recusaDaEdicao !== null && campoDaRecusaDaEdicao === 'telefone'
                      ? 'contato-recusa-do-nome'
                      : null
                  }
                  desabilitado={salvando}
                />
                {sugerido ? (
                  <p className="contato__dica">{SUGESTAO_DO_WHATSAPP}</p>
                ) : null}
                {recusaDaEdicao === null ? null : (
                  <p className="contato__erro" role="alert" id="contato-recusa-do-nome">
                    {recusaDaEdicao}
                  </p>
                )}
                <div className="contato__botoes">
                  <button type="submit" className="contato__botao" disabled={salvando}>
                    {salvando ? SALVANDO : SALVAR}
                  </button>
                  <button
                    type="button"
                    className="contato__acao"
                    disabled={salvando}
                    onClick={() => {
                      setRascunho(null)
                      setSugerido(false)
                      setRecusaDaEdicao(null)
                    }}
                  >
                    {CANCELAR_A_EDICAO}
                  </button>
                </div>
              </form>
            )}
            {rascunho === null && podeEditar ? (
              <button
                type="button"
                className="contato__acao"
                onClick={() => {
                  abrirAEdicao(pessoa)
                }}
              >
                {EDITAR}
              </button>
            ) : null}

            {/* As etiquetas da Pessoa (13.6): gravam na hora, fora da edição dos quatro campos. */}
            <EtiquetasDoAlvo
              alvo={{ tipo: 'pessoa', id: pessoa.id }}
              nomeDoAlvo={pessoa.nome}
              etiquetaIds={etiquetaIdsDe(pessoa)}
              podeEditar={podeEditar}
            />

            <NegociosSemEmpresa negocios={negociosSemEmpresa} />

            {blocoDaEspera}

            {solta ? null : (
              <>
                <EmpresasDaPessoa empresaIds={empresaIdsDe(pessoa) ?? []} />
                {/* 13.19c2: o atraso é a conversa ainda sem Empresa nenhuma — e não um `empresa_id`
                    diferente, que a Pessoa de várias Empresas teria sempre — **e não recortada**
                    (code review, B): o nulo da Pessoa compartilhada é a regra, e não atraso. */}
                {(empresaIdsDe(pessoa)?.length ?? 0) > 0 &&
                conversa.empresa_id === null &&
                (conversa as { empresa_recortada?: boolean }).empresa_recortada !== true ? (
                  <p className="contato__nota">{ATRASO_DA_REPLICA}</p>
                ) : null}
              </>
            )}

            {/* A permissão, e não a edição: com o modal aberto a Pessoa deixa de ser solta no
                meio da cadeia (a adoção), e a seção precisa continuar montada. */}
            {/* 13.20b2: enquanto o recorte por Pessoa está no ar, o vincular espera — sem ele não
                se sabe se a Empresa vai ao Negócio ou à Pessoa; depois da definição, o que se
                mostra é o "vinculando…". */}
            {/* 13.20b2: o N0 que quem age define abre a definição mesmo para quem lê o contato só
                pelo ramo (definir não escreve na Pessoa, B4); fora dele, o vincular de antes.
                Nenhum dos dois enquanto o recorte está no ar, falhou, ou a espera está de pé. */}
            {pessoaId !== null &&
            doContato.isSuccess &&
            !aguardando &&
            (negocioQueQuemAgeDefine !== undefined ||
              (temAPermissao &&
                !soLeitura &&
                (solta || vinculoEmCurso.modalAberto || vinculoEmCurso.ocupado))) ? (
              <VinculoDaPessoaSolta
                prefixo="contato"
                idDoCampo="contato-empresa"
                pessoaId={pessoaId}
                relerAPessoa={relerAPessoa}
                nomeDaFicha={razaoDaFicha}
                telefoneDaFicha={telefoneDaConversaNaFicha(conversa.numero)}
                aoMudarOcupacao={aoMudarOcupacao}
                aoRecusar={setRecusaDoVinculo}
                aoVincular={async (empresa) => {
                  aoVincular(empresa.nome)
                  await clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_CONVERSAS })
                }}
                {...(negocioQueQuemAgeDefine === undefined
                  ? {}
                  : {
                      definirNoNegocio: async (empresa: EmpresaEscolhida) => {
                        await definicao.mutateAsync(empresa.id)
                      },
                    })}
              />
            ) : null}
          </>
        )}
      </div>
    </aside>
  )
}
